import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { useAnchorPosition } from '../../lib/useAnchorPosition';

type SelectContextValue = {
  value: string | undefined;
  onValueChange: ((value: string) => void) | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
  labels: Map<string, React.ReactNode>;
  registerLabel: (value: string, label: React.ReactNode) => void;
  triggerEl: HTMLElement | null;
  setTriggerEl: (el: HTMLElement | null) => void;
};

const SelectContext = createContext<SelectContextValue | null>(null);

function useSelectContext() {
  const ctx = useContext(SelectContext);
  if (!ctx) throw new Error('Select components must be used within Select');
  return ctx;
}

export function Select({
  value,
  onValueChange,
  children,
}: {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [labels] = useState(() => new Map<string, React.ReactNode>());
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null);

  return (
    <SelectContext.Provider
      value={{
        value,
        onValueChange,
        open,
        setOpen,
        labels,
        registerLabel: (v, l) => labels.set(v, l),
        triggerEl,
        setTriggerEl,
      }}
    >
      {children}
    </SelectContext.Provider>
  );
}

export function SelectTrigger({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { open, setOpen, setTriggerEl } = useSelectContext();
  return (
    <button
      type="button"
      ref={setTriggerEl}
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      className={cn(
        'flex w-full items-center justify-between rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-2 text-sm text-[var(--ink-1)]',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function SelectValue({ placeholder }: { placeholder?: string }) {
  const { value, labels } = useSelectContext();
  return <span>{value ? (labels.get(value) ?? value) : (placeholder ?? 'Select…')}</span>;
}

export function SelectContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { open, setOpen, triggerEl } = useSelectContext();
  const pos = useAnchorPosition(triggerEl, open);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[400]" onClick={() => setOpen(false)} />
      <div
        className={cn(
          'fixed z-[401] overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1 shadow-[var(--sh-3)]',
          className,
        )}
        style={{
          top: pos.top,
          left: pos.left,
          minWidth: pos.width > 0 ? pos.width : undefined,
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

export function SelectItem({
  value,
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { value: string }) {
  const ctx = useSelectContext();
  React.useEffect(() => {
    ctx.registerLabel(value, children);
  }, [children, ctx, value]);

  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-center rounded-[var(--r-sm)] px-3 py-2 text-left text-sm text-[var(--ink-1)] hover:bg-[var(--cream-0)]',
        ctx.value === value && 'bg-[var(--cream-0)]',
        className,
      )}
      onClick={() => {
        ctx.onValueChange?.(value);
        ctx.setOpen(false);
      }}
      {...props}
    >
      {children}
    </button>
  );
}
